/**
 * Correzione di uno scarico: peso e/o MATERIALE (#121/#122).
 *
 * Era EditPesoScaricoModal: il nome è cambiato con l'aggiunta del materiale,
 * perché «EditPeso» sarebbe diventato un nome che mente su cosa fa.
 *
 * Il backend registra rettifiche append-only, mai riscritture. Due errori
 * hanno una resa dedicata perché il loro detail è un OGGETTO e passarlo a
 * `setError` finirebbe in JSX (React #31):
 *  - 422 saldo insufficiente → i kg mancanti, che sono l'unica cosa utile;
 *  - 409 già registrato su Ecomatico → il procedimento in tre passi, di cui il
 *    primo avviene FUORI dal gestionale: va distinto, o l'utente lo cerca a
 *    video come gli altri due.
 *
 * Usa `ui/Modal` (overlay, ESC, focus al mount, role=dialog): l'intestazione a
 * più righe e il blocco Ecomatico stanno nel BODY, non nell'header, quindi
 * passano come children senza chiedere nulla al componente generico.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, AlertTriangle, Layers } from 'lucide-react';
import { updateScarico, type ScaricoListItem } from '../../api/magazzino';
import { fmtKg, parseSaldoInsufficiente } from '../../lib/saldo';
import { parseScaricoRegistrato } from '../../lib/prelievo';
import Modal from '../ui/Modal';

interface Props {
  scarico: ScaricoListItem;
  /** Materiali con giacenza nell'area, per spostare il prelievo su un altro CER. */
  materialiArea: string[];
  /** L'area per esteso. La lista scarichi porta solo il codice, e un « A » nudo
   *  non dice a nessuno quale movimento sta correggendo. La descrizione ce l'ha
   *  gia' la pagina chiamante, caricata per il filtro: si passa di la'. */
  area?: { codice: string; descrizione: string } | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function EditScaricoModal({ scarico, materialiArea, area, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [peso, setPeso] = useState(String(scarico.peso_prelevato_kg));
  const [materiale, setMateriale] = useState(scarico.materiale);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registratoAt, setRegistratoAt] = useState<string | null>(null);

  // Il materiale corrente resta sempre scegliibile anche se in area non ha più
  // giacenza: è lo stato di partenza, non una destinazione.
  const opzioni = Array.from(new Set([scarico.materiale, ...materialiArea]));

  // Confronto NUMERICO e non testuale: "1000" e "1000.0" sono lo stesso peso, e
  // dirli diversi rimetterebbe il Salva a disposizione su una non-modifica —
  // che dopo un rifiuto Ecomatico significa riprovare la stessa cosa.
  const invariato = materiale === scarico.materiale
    && Number(peso) === scarico.peso_prelevato_kg;

  const dataScarico = new Date(scarico.created_at).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const areaEstesa = area ? `${area.codice} — ${area.descrizione}` : (scarico.area_codice ?? '—');
  // Righe sorelle: si mostra solo se il server le dichiara. Dedurle dai dati
  // della lista (stesso istante, stessa area) sarebbe un'euristica che con un
  // filtro CER attivo conta meno righe di quante ce ne sono, e mentirebbe
  // proprio sul punto per cui questa riga esiste.
  const altreRighe = (scarico.gruppo_righe ?? 0) - 1;

  async function save() {
    const n = Number(peso);
    if (!Number.isFinite(n) || n <= 0) { setError(t('magazzino.edit_scarico.peso_non_valido')); return; }
    setBusy(true); setError(null); setRegistratoAt(null);
    try {
      await updateScarico(scarico.id, {
        peso_prelevato_kg: n,
        ...(materiale !== scarico.materiale ? { materiale } : {}),
      });
      onSaved();
    } catch (err: unknown) {
      const registrato = parseScaricoRegistrato(err);
      if (registrato) { setRegistratoAt(registrato.registrato_at); return; }
      const saldo = parseSaldoInsufficiente(err);
      if (saldo) {
        setError(t(`magazzino.saldo.${saldo.code}`, {
          materiale: saldo.materiale, area: saldo.area_codice ?? '',
          disponibile: fmtKg(saldo.disponibile_kg), richiesto: fmtKg(saldo.richiesto_kg),
          mancante: fmtKg(saldo.mancante_kg),
        }));
        return;
      }
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('magazzino.edit_scarico.errore_modifica'));
    } finally { setBusy(false); }
  }

  // Chiusura implicita (ESC, click sull'overlay, X) bloccata mentre la PATCH
  // è in volo: chiudere a metà di un salvataggio lascia l'utente senza sapere
  // se ha salvato, e su un registro append-only quel dubbio costa una verifica
  // a mano. L'Annulla in footer è invece disabilitato: visibile, non silenzioso.
  // Il click sull'overlay non era guardato nemmeno prima della migrazione.
  const chiudi = () => { if (!busy) onClose(); };

  const dataRegistrazione = registratoAt
    ? new Date(registratoAt).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';

  return (
    <Modal
      title={t('magazzino.edit_scarico.titolo')}
      onClose={chiudi}
      maxWidth="max-w-md"
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={busy}
            className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white disabled:opacity-60">
            {t('common.azioni.annulla')}
          </button>
          <button type="button" onClick={save} disabled={busy || invariato} data-testid="edit-scarico-salva"
            className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark disabled:opacity-60">
            {busy ? t('magazzino.edit_scarico.salvataggio') : t('magazzino.edit_scarico.salva')}
          </button>
        </>
      )}
    >
      <div className="px-3 py-2.5 bg-stone-bg border border-stone-mid rounded space-y-1"
           data-testid="edit-scarico-intestazione">
        <InfoRiga label={t('magazzino.edit_scarico.info_data')} valore={dataScarico} />
        <InfoRiga label={t('magazzino.edit_scarico.info_area')} valore={areaEstesa} />
        <InfoRiga label={t('magazzino.edit_scarico.info_operatore')}
          valore={scarico.operatore_nome ?? '—'} />
        {scarico.gruppo_id && altreRighe > 0 && (
          <p className="flex gap-2 pt-1.5 mt-1.5 border-t border-stone-mid text-xs text-amber-800"
             data-testid="edit-scarico-gruppo">
            <Layers size={14} className="flex-shrink-0 mt-0.5" />
            <span>{t('magazzino.edit_scarico.info_gruppo', { count: altreRighe })}</span>
          </p>
        )}
      </div>

      <div>
        <label htmlFor="edit-scarico-materiale" className="block text-xs font-medium text-ink-soft mb-1">
          {t('magazzino.edit_scarico.materiale_label')}
        </label>
        <select
          id="edit-scarico-materiale"
          data-testid="edit-scarico-materiale"
          className="w-full text-sm border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage"
          value={materiale}
          onChange={(e) => setMateriale(e.target.value)}
        >
          {opzioni.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        {materiale !== scarico.materiale && (
          <p className="text-xs text-ink-soft mt-1" data-testid="edit-scarico-nota-materiale">
            {t('magazzino.edit_scarico.nota_materiale', {
              vecchio: scarico.materiale, nuovo: materiale,
              kg: fmtKg(scarico.peso_prelevato_kg),
            })}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="edit-scarico-peso" className="block text-xs font-medium text-ink-soft mb-1">
          {t('magazzino.edit_scarico.peso_label')}
        </label>
        <input id="edit-scarico-peso" data-testid="edit-scarico-peso" type="number" step="0.01"
          className="w-full text-sm border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage"
          value={peso} onChange={(e) => setPeso(e.target.value)} />
      </div>

      {/* Già registrato su Ecomatico: il divieto da solo non basta, serve la
          strada. Il passo 1 è marcato come esterno al gestionale. */}
      {registratoAt && (
        <div className="px-3 py-3 bg-amber-50 border border-amber-200 rounded space-y-2"
             data-testid="edit-scarico-ecomatico">
          <p className="text-sm text-amber-900 flex gap-2">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
            {t('magazzino.edit_scarico.ecomatico_titolo', { data: dataRegistrazione })}
          </p>
          <ol className="text-sm text-amber-900 space-y-1 pl-1">
            <li className="flex gap-2">
              <span className="font-semibold">1.</span>
              <span>
                {t('magazzino.edit_scarico.ecomatico_passo1')}
                <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-200/70 text-[11px] font-medium whitespace-nowrap">
                  <ExternalLink size={11} /> {t('magazzino.edit_scarico.fuori_gestionale')}
                </span>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold">2.</span>
              <span>{t('magazzino.edit_scarico.ecomatico_passo2')}</span>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold">3.</span>
              <span>{t('magazzino.edit_scarico.ecomatico_passo3')}</span>
            </li>
          </ol>
        </div>
      )}

      {error && <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">{error}</div>}
    </Modal>
  );
}

function InfoRiga({ label, valore }: { label: string; valore: string }) {
  return (
    <p className="flex justify-between gap-3 text-xs">
      <span className="text-ink-soft">{label}</span>
      <span className="text-ink-dark font-medium text-right">{valore}</span>
    </p>
  );
}
